import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, Check, Loader2, Shield, Dumbbell, Utensils, TrendingUp, Brain, XCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const FEATURES = [
  { icon: Dumbbell, text: "85+ øvelser med YouTube video-tutorials" },
  { icon: TrendingUp, text: "Træningstracker med historik og statistik" },
  { icon: Utensils, text: "Madplaner med makro- og kalorieberegning" },
  { icon: Sparkles, text: "AI Coach — generér personlige programmer" },
  { icon: Brain, text: "Inspiration, tips og ekspertviden" },
  { icon: Shield, text: "Automatisk fornyelse via Stripe" },
];

export default function UpgradePage() {
  const { authHeaders, subscription, refreshSubscription } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [pollingDone, setPollingDone] = useState(false);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error("Kunne ikke bekræfte betaling. Prøv igen.");
      setPollingDone(true);
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`, { headers: authHeaders() });
      if (res.data.payment_status === "paid") {
        toast.success("Betaling gennemført! Dit abonnement er nu aktivt.");
        refreshSubscription();
        setPollingDone(true);
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    }
  }, [authHeaders, refreshSubscription]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId && !pollingDone) {
      pollStatus(sessionId);
    }
  }, [searchParams, pollStatus, pollingDone]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/checkout/subscribe`, { origin_url: origin }, { headers: authHeaders() });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved oprettelse af checkout");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Er du sikker på du vil annullere dit abonnement? Du beholder adgang til periodens udløb.")) {
      return;
    }
    setCancelLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/cancel`, {}, { headers: authHeaders() });
      toast.success(res.data.message);
      refreshSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke annullere abonnement");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async () => {
    setCancelLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/reactivate`, {}, { headers: authHeaders() });
      toast.success(res.data.message);
      refreshSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke genaktivere abonnement");
    } finally {
      setCancelLoading(false);
    }
  };

  const isActive = subscription?.has_access;
  const isRecurring = subscription?.subscription_type === "recurring";
  const isCanceling = subscription?.subscription_canceling;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fade-in-up" data-testid="upgrade-page">
      <div className="text-center mb-10">
        <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl sm:text-4xl uppercase tracking-tight text-white mb-2">
          Fitrack<span className="text-[#DFFF00]">R</span> Pro
        </h1>
        <p className="text-sm text-zinc-400">Alt du behøver for din fitnessrejse</p>
      </div>

      <Card className="bg-[#18181b] border-[#DFFF00]/20 rounded-sm overflow-hidden">
        <div className="bg-[#DFFF00]/5 border-b border-[#DFFF00]/10 px-6 py-4 text-center">
          <div className="flex items-center justify-center gap-1">
            <span style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-5xl font-bold text-[#DFFF00]">49</span>
            <div className="text-left">
              <span className="text-lg text-zinc-300 block leading-tight">kr</span>
              <span className="text-xs text-zinc-500 block">/måned</span>
            </div>
          </div>
          <p className="text-xs text-zinc-400 mt-2">
            {isRecurring ? "Fornyes automatisk hver måned" : "De første 3 dage er helt gratis"}
          </p>
        </div>

        <CardContent className="p-6">
          <div className="space-y-3 mb-8">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-[#DFFF00]/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-[#DFFF00]" />
                </div>
                <span className="text-sm text-zinc-300">{text}</span>
              </div>
            ))}
          </div>

          {isActive ? (
            <div className="text-center space-y-4">
              <div className={`flex items-center justify-center gap-2 ${isCanceling ? 'bg-orange-500/10 border-orange-500/20' : 'bg-green-500/10 border-green-500/20'} border rounded-sm px-4 py-3`}>
                <Check className={`h-5 w-5 ${isCanceling ? 'text-orange-400' : 'text-green-400'}`} />
                <span className={`text-sm ${isCanceling ? 'text-orange-400' : 'text-green-400'} font-bold`}>
                  {subscription?.trial_active
                    ? `Prøveperiode aktiv — ${subscription.trial_days_left} dage tilbage`
                    : isCanceling
                      ? "Abonnement annulleret"
                      : "Abonnement aktivt"}
                </span>
              </div>
              
              {subscription?.trial_active && (
                <p className="text-xs text-zinc-500">Dit abonnement starter automatisk efter prøveperioden</p>
              )}
              
              {isCanceling && subscription?.subscription_cancel_at && (
                <p className="text-xs text-zinc-400">
                  Du har adgang indtil: <span className="text-[#DFFF00]">{new Date(subscription.subscription_cancel_at).toLocaleDateString("da-DK")}</span>
                </p>
              )}
              
              {isRecurring && !subscription?.trial_active && (
                <div className="pt-2">
                  {isCanceling ? (
                    <Button onClick={handleReactivate} disabled={cancelLoading} data-testid="reactivate-btn"
                      className="w-full bg-[#DFFF00] text-black hover:bg-[#C8E600] rounded-sm font-bold uppercase tracking-wider h-12">
                      {cancelLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                      Genaktiver abonnement
                    </Button>
                  ) : (
                    <Button onClick={handleCancel} disabled={cancelLoading} variant="outline" data-testid="cancel-subscription-btn"
                      className="w-full border-zinc-700 text-zinc-400 hover:text-white hover:border-red-500/50 hover:bg-red-500/10 rounded-sm h-10 text-sm">
                      {cancelLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}
                      Annuller abonnement
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Button onClick={handleSubscribe} disabled={loading} data-testid="subscribe-btn"
              className="w-full bg-[#DFFF00] text-black hover:bg-[#C8E600] hover:shadow-[0_0_20px_rgba(223,255,0,0.3)] rounded-sm font-bold uppercase tracking-wider h-14 text-base">
              {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Indlæser...</> : "Abonner nu — 49 kr/md"}
            </Button>
          )}
        </CardContent>
      </Card>

      <p className="text-center text-xs text-zinc-600 mt-6">
        Sikker betaling via Stripe. {isRecurring ? "Fornyes automatisk. " : ""}Du kan annullere til enhver tid.
      </p>
    </div>
  );
}
